import React, { useEffect, useMemo, useState } from "react";
import { getBranches } from "../api";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { normalizePaginatedResponse } from "../utils/pagination";

const ReportBranchPicker = ({ value, onChange, label = "Sucursal", className = "" }) => {
  const { user } = useCurrentUser();
  const canViewAll = hasPermission(user, "branch.access.any");
  const canReadBranches = hasPermission(user, "branch.read") || canViewAll;
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!canReadBranches) return;
    let isMounted = true;
    const fetchBranches = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getBranches({
          pageSize: 0,
          sortBy: "createdAt",
          sortOrder: "asc",
        });
        const normalized = normalizePaginatedResponse(data);
        const ordered = [...(normalized.data || [])].sort((a, b) => {
          const aDate = new Date(a?.createdAt || 0).getTime();
          const bDate = new Date(b?.createdAt || 0).getTime();
          const aValidDate = Number.isFinite(aDate) && aDate > 0;
          const bValidDate = Number.isFinite(bDate) && bDate > 0;
          if (aValidDate && bValidDate && aDate !== bDate) return aDate - bDate;
          if (aValidDate && !bValidDate) return -1;
          if (!aValidDate && bValidDate) return 1;
          return Number(a?.id || 0) - Number(b?.id || 0);
        });
        if (isMounted) {
          setBranches(ordered);
        }
      } catch (err) {
        console.error("Error fetching branches:", err);
        if (isMounted) {
          setError("No se pudieron cargar las sucursales.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchBranches();
    return () => {
      isMounted = false;
    };
  }, [canReadBranches]);

  const options = useMemo(() => {
    const branchOptions = branches.map((branch) => ({
      id: String(branch.id),
      label: branch.branchName || `Sucursal ${branch.id}`,
    }));
    return canViewAll
      ? [{ id: "all", label: "Todas" }, ...branchOptions]
      : branchOptions;
  }, [branches, canViewAll]);

  const selectedOption = useMemo(() => {
    const selected = options.find((option) => option.id === String(value));
    if (selected) return selected;
    if (!value) return null;
    return {
      id: String(value),
      label: String(value) === "all" ? "Todas" : `Sucursal ${value}`,
    };
  }, [options, value]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        normalizedQuery ? option.label.toLowerCase().includes(normalizedQuery) : true
      ),
    [options, normalizedQuery]
  );

  const handleSelect = (nextValue) => {
    onChange?.(nextValue);
    setIsOpen(false);
    setQuery("");
  };

  if (!canReadBranches) return null;

  if (!branches.length && !loading) {
    return (
      <div className={`text-xs text-gray-500 ${className}`.trim()}>
        No hay sucursales disponibles.
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-2 ${className}`.trim()}>
      <label className="text-xs text-gray-600">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={isOpen ? query : selectedOption?.label || ""}
          onFocus={() => {
            setIsOpen(true);
            setQuery("");
          }}
          onBlur={() => {
            setTimeout(() => {
              setIsOpen(false);
              setQuery("");
            }, 120);
          }}
          onChange={(event) => {
            setIsOpen(true);
            setQuery(event.target.value);
          }}
          placeholder="Buscar sucursal..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        {isOpen && (
          <div className="absolute z-40 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-64 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500">Sin resultados</div>
            ) : (
              filteredOptions.map((option) => {
                const isActive = String(value) === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onMouseDown={() => handleSelect(option.id)}
                    className={`w-full text-left px-3 py-2 text-sm transition ${
                      isActive
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
      {loading && <span className="text-xs text-gray-500">Cargando sucursales...</span>}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
};

export default ReportBranchPicker;
